'use client'
// TYPE-ONLY import from the /schema subpath. The @sifly/booking-flows barrel
// re-exports hash.ts, which imports node:crypto — a value import here would
// break the browser bundle.
import type { PostCheckoutFlow } from '@sifly/booking-flows/schema'
import { ContentStep } from './steps/ContentStep'
import { WaiverStep } from './steps/WaiverStep'
import { makeSignFn } from './signAdapter'
import type { FlowBooking, FlowCapability, WaiverPolicy } from './types'

type Props = {
  /** The PINNED definition: the checkout response in-page, the version row on
   *  the durable page. Never re-resolved client-side. */
  flow: PostCheckoutFlow
  capability: FlowCapability
  booking: FlowBooking
  waiverTerms: string | null
  policy: WaiverPolicy
  onAllSigned?: () => void
}

/**
 * Walks flow.steps in order. Single-page linear render (content cards, then the
 * waiver gate) — a wizard is over-engineering for a two-step flow. The booking
 * summary card stays as chrome in the parent; the runner renders steps only.
 */
export function BookingFlowRunner({ flow, capability, booking, waiverTerms, policy, onAllSigned }: Props) {
  const sign = makeSignFn(capability)

  return (
    <div className="space-y-4">
      {flow.steps.map(step => {
        switch (step.kind) {
          case 'content':
            return <ContentStep key={step.key} title={step.data.title} markdown={step.data.markdown} />
          case 'sign_waiver':
            return (
              <WaiverStep
                key={step.key}
                mode={booking.mode}
                requiredRiders={booking.requiredRiders}
                initialSignedSlots={booking.signedSlots}
                waiverTerms={waiverTerms}
                minRiderAge={policy.minRiderAge}
                allowMinors={policy.allowMinors}
                sign={sign}
                onAllSigned={onAllSigned}
              />
            )
          default: {
            // Adding a step kind must fail the build here, not render nothing.
            const _exhaustive: never = step
            void _exhaustive
            return null
          }
        }
      })}
    </div>
  )
}
